/**
 * Phase 27.72C — GHL Web Chat Widget — consent-gated marketing injection.
 *
 * Audit Codex : avant cette phase, le script GHL était chargé en static dans
 * index.html (<script src="widgets.leadconnectorhq.com/loader.js" defer>), AVANT
 * tout consent utilisateur. C'est une violation Loi 25 Art. 9 (Québec) qui
 * exige un consentement explicite ACTIF avant tout chargement de tracking
 * tiers (le widget collecte name+email+conversation et POST vers serveurs USA).
 *
 * Aligné avec le pattern existant (meta-pixel.ts / ga4.ts / clarity.ts) :
 * - Consent-gated : appelé UNIQUEMENT si `categories.marketing === true`
 *   (cf consent-gate.tsx applyConsent)
 * - Idle-deferred via idleInject (jamais bloque le critical path)
 * - Guard `loaded` flag pour éviter double injection
 */
import { idleInject } from '@/lib/perf/idle-inject';

const WIDGET_LOADER_URL = 'https://widgets.leadconnectorhq.com/loader.js';
const WIDGET_RESOURCES_URL = 'https://widgets.leadconnectorhq.com/chat-widget/loader.js';
const WIDGET_ID = '6a160e901ce15bb9e92d2f95';
const SCRIPT_ID = 'ghl-chat-widget-loader';
const MOBILE_OFFSET_STYLE_ID = 'oceanne-ghl-mobile-offset';

let loaded = false;

/**
 * Phase 27.72F — injection CSS dans Shadow DOM du widget GHL pour offset mobile.
 *
 * Le widget GHL est un Web Component (<chat-widget>) avec Shadow DOM encapsulé.
 * Le bubble button `.lc_text-widget--bubble` est positionné `fixed bottom: 20px
 * right: 20px` INSIDE le shadow root — inaccessible aux CSS rules du document
 * parent (shadow DOM encapsule les styles).
 *
 * Solution : on append un <style> directement DANS le shadow root du widget,
 * ce qui permet d'override les positions du bubble/prompt en mobile (<768px)
 * pour éviter l'overlap avec MobileStickyBar (qui occupe les 68px bas).
 *
 * Retry polling : le widget shadow DOM se populate async après injection script.
 * On poll 250ms × 40 essais (~10s max) jusqu'à succès, puis stop.
 */
function applyMobileOffsetToShadowDom(): boolean {
  const chatWidget = document.querySelector('chat-widget');
  if (!chatWidget || !chatWidget.shadowRoot) return false;

  // Already injected ?
  if (chatWidget.shadowRoot.querySelector(`#${MOBILE_OFFSET_STYLE_ID}`)) return true;

  const style = document.createElement('style');
  style.id = MOBILE_OFFSET_STYLE_ID;
  style.textContent = `
    @media (max-width: 767px) {
      /* Bubble button + prompt panel offset above MobileStickyBar (~68px + 20px gap) */
      .lc_text-widget,
      .lc_text-widget--bubble,
      .lc_text-widget--prompt {
        bottom: 88px !important;
      }
    }
  `;
  chatWidget.shadowRoot.appendChild(style);
  return true;
}

function startMobileOffsetPolling(): void {
  if (typeof window === 'undefined') return;
  let attempts = 0;
  const maxAttempts = 40; // ~10s total at 250ms intervals
  const interval = window.setInterval(() => {
    attempts++;
    if (applyMobileOffsetToShadowDom() || attempts >= maxAttempts) {
      window.clearInterval(interval);
    }
  }, 250);
}

export function injectGhlWidget(): void {
  if (typeof document === 'undefined') return;
  if (loaded) return;
  if (document.getElementById(SCRIPT_ID)) {
    loaded = true;
    startMobileOffsetPolling();
    return;
  }
  loaded = true;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = WIDGET_LOADER_URL;
  script.defer = true;
  script.setAttribute('data-resources-url', WIDGET_RESOURCES_URL);
  script.setAttribute('data-widget-id', WIDGET_ID);
  document.body.appendChild(script);

  // Polling pour appliquer offset mobile quand le shadow DOM est populé
  startMobileOffsetPolling();
}

export function deferGhlWidget(): void {
  idleInject(() => injectGhlWidget());
}
